import { __export, positionAt, offsetAt, normalizeText, jsonValidator, exportText, documentVersion, applyEdits, TextRange, TextEdit, SemanticToken, Selection, OutMessage, Origin, Options, Operation, EditorSnapshot, DocumentVersion, Document, DiagnosticBatch, Diagnostic, CompletionItem, init, validOffset, validDiagnostics, validSelection, jsonDiagnostics } from './chunk-3I3YYHFN.js';
import { Schema, Effect, Stream, Queue } from 'effect';
import { HoverSource, Completion, SemanticToken as SemanticToken$1, diagnosticsAt, mostSevere, optionId, CompletionPopup, HoverPopup, isOver, segments, offsetAtPoint } from '@foldworks/text-intelligence';
import { defineMessageUnion } from 'foldkit/message';
import { Command, Mount } from 'foldkit';

// src/code-editor.ts
var code_editor_exports = {};
__export(code_editor_exports, {
  CompletionItem: () => CompletionItem,
  Diagnostic: () => Diagnostic,
  DiagnosticBatch: () => DiagnosticBatch,
  Document: () => Document,
  DocumentVersion: () => DocumentVersion,
  EditorSnapshot: () => EditorSnapshot,
  Message: () => Message,
  Model: () => Model,
  Operation: () => Operation,
  Options: () => Options,
  Origin: () => Origin,
  OutMessage: () => OutMessage,
  Selection: () => Selection,
  SemanticToken: () => SemanticToken,
  TextEdit: () => TextEdit,
  TextRange: () => TextRange,
  applyEdits: () => applyEdits,
  documentVersion: () => documentVersion,
  execute: () => execute,
  exportText: () => exportText,
  implementation: () => implementation,
  init: () => init2,
  jsonValidator: () => jsonValidator,
  normalizeText: () => normalizeText,
  offsetAt: () => offsetAt,
  positionAt: () => positionAt,
  snapshot: () => snapshot,
  update: () => update,
  view: () => view,
  visibleLines: () => visibleLines
});

// src/native/yaml-tokenize.ts
var tokenizeYamlLine = (text, incoming) => {
  const tokens = [];
  const indent = /^ */.exec(text)[0].length;
  if (incoming.startsWith("yaml-block:") && (!text.trim() || indent > Number(incoming.slice(11)))) {
    return {
      text,
      incoming,
      outgoing: incoming,
      tokens: [{ from: 0, to: text.length, kind: "string" }]
    };
  }
  let state = incoming === "yaml-single" || incoming === "yaml-double" ? incoming : "code";
  let i = 0;
  const emit = (from, kind) => {
    if (i > from) tokens.push({ from, to: i, kind });
  };
  const quoted2 = (quote) => {
    while (i < text.length) {
      if (quote === '"' && text[i] === "\\" || quote === "'" && text.slice(i, i + 2) === "''") {
        i = Math.min(text.length, i + 2);
        continue;
      }
      if (text[i++] === quote) {
        state = "code";
        return;
      }
    }
    state = quote === "'" ? "yaml-single" : "yaml-double";
  };
  while (i < text.length) {
    const from = i;
    if (state === "yaml-single" || state === "yaml-double") {
      quoted2(state === "yaml-single" ? "'" : '"');
      emit(from, "string");
    } else if (/\s/.test(text[i])) {
      while (i < text.length && /\s/.test(text[i])) i++;
      emit(from, "plain");
    } else if (text[i] === "#" && (i === 0 || /\s/.test(text[i - 1]))) {
      i = text.length;
      emit(from, "comment");
    } else if (["'", '"'].includes(text[i])) {
      const quote = text[i++];
      quoted2(quote);
      emit(from, /^\s*:(?:\s|$|[\[\]{},])/.test(text.slice(i)) ? "property" : "string");
    } else if (/^[|>][1-9+-]{0,2}(?:\s+#.*)?\s*$/.test(text.slice(i))) {
      const indicator = /[1-9]/.exec(text.slice(i))?.[0];
      const base = indent + (/^\s*-\s/.test(text) ? 2 : 0);
      state = `yaml-block:${base + (indicator ? Number(indicator) - 1 : 0)}`;
      while (i < text.length && !/\s/.test(text[i])) i++;
      emit(from, "punctuation");
    } else if (/^(?:---|\.\.\.)(?:\s|$)/.test(text.slice(i))) {
      i += 3;
      emit(from, "punctuation");
    } else if (/[\[\]{},]/.test(text[i]) || /^[-?:](?:\s|$)/.test(text.slice(i))) {
      i++;
      emit(from, "punctuation");
    } else if (/^[&*!]/.test(text.slice(i))) {
      i += /^[^\s,\[\]{}]+/.exec(text.slice(i))[0].length;
      emit(from, "type");
    } else {
      const key = /^[^\s'"\[\]{},#][^\[\]{},#]*?(?=:(?:\s|$|[\[\]{},]))/.exec(text.slice(i));
      if (key) {
        i += key[0].length;
        emit(from, "property");
        i++;
        emit(i - 1, "punctuation");
      } else {
        const word = /^[^\s,\[\]{}]+/.exec(text.slice(i))[0];
        i += word.length;
        emit(
          from,
          /^(?:true|false|null|~)$/i.test(word) ? "keyword" : /^[-+]?(?:0x[\da-f]+|0o[0-7]+|(?:\d+(?:\.\d*)?|\.\d+)(?:e[-+]?\d+)?|\.inf|\.nan)$/i.test(
            word
          ) ? "number" : "string"
        );
      }
    }
  }
  return { text, incoming, outgoing: state, tokens };
};

// src/native/tokenize.ts
var TokenKind = Schema.Literals([
  "plain",
  "keyword",
  "string",
  "number",
  "comment",
  "type",
  "property",
  "punctuation"
]);
var LexState = Schema.Union([
  Schema.Literals(["code", "comment", "template", "yaml-single", "yaml-double"]),
  Schema.TemplateLiteral(["yaml-block:", Schema.Number])
]);
var Token = Schema.Struct({ from: Schema.Number, to: Schema.Number, kind: TokenKind });
var Line = Schema.Struct({
  text: Schema.String,
  incoming: LexState,
  outgoing: LexState,
  tokens: Schema.Array(Token)
});
var keywords = new Set(
  "as async await break case catch class const continue debugger default delete do else enum export extends false finally for from function if implements import in instanceof interface let new null of private protected public readonly return satisfies static super switch this throw true try type typeof undefined var void while yield".split(
    " "
  )
);
var tokenizeLine = (text, language, incoming) => {
  if (language === "yaml" || language === "yml") return tokenizeYamlLine(text, incoming);
  if (language === "text" || !["json", "javascript", "typescript", "jsx", "tsx"].includes(language)) {
    return {
      text,
      incoming: "code",
      outgoing: "code",
      tokens: [{ from: 0, to: text.length, kind: "plain" }]
    };
  }
  const tokens = [];
  let state = incoming;
  let i = 0;
  const emit = (from, kind) => {
    if (i > from) tokens.push({ from, to: i, kind });
  };
  const quoted2 = (quote) => {
    while (i < text.length) {
      if (text[i] === "\\") {
        i = Math.min(text.length, i + 2);
        continue;
      }
      if (text[i++] === quote) return true;
    }
    return false;
  };
  while (i < text.length) {
    const from = i;
    if (state === "comment") {
      const end = text.indexOf("*/", i);
      i = end < 0 ? text.length : end + 2;
      if (end >= 0) state = "code";
      emit(from, "comment");
    } else if (state === "template") {
      if (quoted2("`")) state = "code";
      emit(from, "string");
    } else if (language !== "json" && text.startsWith("//", i)) {
      i = text.length;
      emit(from, "comment");
    } else if (language !== "json" && text.startsWith("/*", i)) {
      i += 2;
      const end = text.indexOf("*/", i);
      i = end < 0 ? text.length : end + 2;
      state = end < 0 ? "comment" : "code";
      emit(from, "comment");
    } else if (text[i] === '"' || language !== "json" && ["'", "`"].includes(text[i])) {
      const quote = text[i++];
      const closed = quoted2(quote);
      if (!closed && quote === "`") state = "template";
      emit(from, /^\s*:/.test(text.slice(i)) ? "property" : "string");
    } else if (/[\d]/.test(text[i]) || text[i] === "-" && /\d/.test(text[i + 1] ?? "")) {
      const match = /^-?(?:0[xX][\da-fA-F]+|\d+(?:\.\d*)?(?:[eE][+-]?\d+)?n?)/.exec(text.slice(i));
      i += match?.[0].length ?? 1;
      emit(from, "number");
    } else if (/[\p{L}_$]/u.test(text[i])) {
      const match = /^[\p{L}\p{M}\p{N}_$]+/u.exec(text.slice(i));
      i += match[0].length;
      const word = match[0];
      emit(
        from,
        ["true", "false", "null", "undefined"].includes(word) ? "number" : keywords.has(word) ? "keyword" : /^\s*:/.test(text.slice(i)) ? "property" : /^[A-Z]/.test(word) || ["string", "number", "boolean", "never", "unknown"].includes(word) ? "type" : "plain"
      );
    } else if (/\s/.test(text[i])) {
      while (i < text.length && /\s/.test(text[i])) i++;
      emit(from, "plain");
    } else {
      i += text.codePointAt(i) > 65535 ? 2 : 1;
      emit(from, "punctuation");
    }
  }
  return { text, incoming, outgoing: state, tokens };
};
var tokenize = (text, language, previous = []) => {
  const lines = text.split("\n");
  let suffix = 0;
  while (suffix < Math.min(lines.length, previous.length) && lines[lines.length - 1 - suffix] === previous[previous.length - 1 - suffix].text)
    suffix++;
  let state = "code";
  return lines.map((line, index) => {
    const old = index >= lines.length - suffix ? previous[previous.length - (lines.length - index)] : previous[index];
    const result = old?.text === line && old.incoming === state ? old : tokenizeLine(line, language, state);
    state = result.outgoing;
    return result;
  });
};
var lineStarts = (lines) => {
  let from = 0;
  return lines.map((line) => {
    const start = from;
    from += line.text.length + 1;
    return start;
  });
};
var lineAt = (starts, offset) => {
  let low = 0, high = starts.length;
  while (low + 1 < high) {
    const middle = low + high >> 1;
    if (starts[middle] <= offset) low = middle;
    else high = middle;
  }
  return low;
};

// src/native/model.ts
var HistoryStep = Schema.Struct({
  edits: Schema.Array(TextEdit),
  inverse: Schema.Array(TextEdit),
  before: Selection,
  after: Selection
});
var HistoryGroup = Schema.Struct({
  id: Schema.Number,
  kind: Schema.String,
  time: Schema.Number,
  steps: Schema.Array(HistoryStep)
});
var Model = Schema.Struct({
  id: Schema.String,
  document: Document,
  selection: Selection,
  options: Options,
  lines: Schema.Array(Line),
  starts: Schema.Array(Schema.Number),
  past: Schema.Array(HistoryGroup),
  future: Schema.Array(HistoryGroup),
  nextHistoryId: Schema.Number,
  diagnostics: Schema.Array(DiagnosticBatch),
  viewport: Schema.Struct({ top: Schema.Number, left: Schema.Number, height: Schema.Number }),
  search: Schema.Struct({
    open: Schema.Boolean,
    query: Schema.String,
    replacement: Schema.String,
    caseSensitive: Schema.Boolean
  }),
  /** Host token kinds, carried through edits until the next batch. */
  tokens: Schema.Array(SemanticToken$1),
  /** Suggestions being shown, or `null`. */
  completion: Schema.NullOr(Completion.List),
  hover: Schema.NullOr(Schema.Struct({ offset: Schema.Number, source: HoverSource })),
  goToLine: Schema.String,
  lease: Schema.String,
  composing: Schema.Boolean,
  status: Schema.Literals(["Loading", "Ready", "Failed"]),
  error: Schema.String
});
var init2 = (config) => {
  const base = init(config);
  const lines = tokenize(base.document.text, base.document.languageId);
  return {
    ...base,
    lease: "",
    lines,
    starts: lineStarts(lines),
    past: [],
    future: [],
    nextHistoryId: 1,
    viewport: { top: 0, left: 0, height: 352 },
    search: { open: false, query: "", replacement: "", caseSensitive: true },
    tokens: [],
    completion: null,
    hover: null,
    goToLine: "1",
    composing: false
  };
};
var Action = Schema.Literals([
  "undo",
  "redo",
  "indent",
  "outdent",
  "newline",
  "comment",
  "duplicate",
  "deleteLine",
  "format",
  "findNext",
  "findPrevious",
  "replace",
  "replaceAll",
  "complete",
  "goToLine",
  "focus"
]);
var Identity = { session: Schema.Number, lease: Schema.String };
var Message = defineMessageUnion({
  Mounted: { ...Identity },
  FailedMount: { reason: Schema.String },
  Edited: {
    ...Identity,
    baseRevision: Schema.Number,
    edits: Schema.Array(TextEdit),
    before: Selection,
    selection: Selection,
    kind: Schema.String,
    time: Schema.Number,
    groupId: Schema.Number
  },
  Selected: { ...Identity, revision: Schema.Number, selection: Selection },
  Scrolled: { ...Identity, top: Schema.Number, left: Schema.Number, height: Schema.Number },
  Composition: { ...Identity, active: Schema.Boolean },
  Run: { action: Action },
  Execute: { operation: Operation },
  CompletedCommand: { ...Identity, reason: Schema.String },
  ReplaceDocument: { uri: Schema.String, text: Schema.String, languageId: Schema.String },
  SetOptions: Options.fields,
  SetLanguage: { languageId: Schema.String },
  SetDiagnostics: DiagnosticBatch.fields,
  OpenSearch: { open: Schema.Boolean },
  SearchQuery: { query: Schema.String },
  SearchReplacement: { replacement: Schema.String },
  ToggleCase: {},
  OpenCompletion: { open: Schema.Boolean },
  MoveCompletion: { delta: Schema.Number },
  ChooseCompletion: { index: Schema.Number },
  GoToLine: { value: Schema.String },
  Reveal: { selection: Selection },
  /** The pointer rested on a character, or left the text; or the caret asked. */
  Hovered: { ...Identity, revision: Schema.Number, offset: Schema.NullOr(Schema.Number), source: HoverSource },
  DismissedHover: {}
});
var bounds = (selection) => ({
  from: Math.min(selection.anchor, selection.head),
  to: Math.max(selection.anchor, selection.head)
});
var collapsed = (offset) => ({ anchor: offset, head: offset });
var difference = (before, after) => {
  if (before === after) return [];
  let from = 0, beforeEnd = before.length, afterEnd = after.length;
  while (from < Math.min(beforeEnd, afterEnd) && before[from] === after[from]) from++;
  while (!validOffset(before, from) || !validOffset(after, from)) from--;
  while (beforeEnd > from && afterEnd > from && before[beforeEnd - 1] === after[afterEnd - 1]) {
    beforeEnd--;
    afterEnd--;
  }
  while (!validOffset(before, beforeEnd) || !validOffset(after, afterEnd)) {
    beforeEnd++;
    afterEnd++;
  }
  return [{ from, to: beforeEnd, insert: after.slice(from, afterEnd) }];
};
var invert = (text, edits) => {
  applyEdits(text, edits);
  let shift = 0;
  return edits.map((edit) => {
    const inverse = {
      from: edit.from + shift,
      to: edit.from + shift + edit.insert.length,
      insert: text.slice(edit.from, edit.to)
    };
    shift += edit.insert.length - (edit.to - edit.from);
    return inverse;
  });
};
var mapOffset = (offset, edits, association = 1) => {
  let shift = 0;
  for (const edit of edits) {
    if (offset < edit.from || offset === edit.from && association < 0) break;
    if (offset <= edit.to) return edit.from + shift + (association < 0 ? 0 : edit.insert.length);
    shift += edit.insert.length - (edit.to - edit.from);
  }
  return offset + shift;
};
var mapSelection = (selection, edits) => ({
  anchor: mapOffset(selection.anchor, edits),
  head: mapOffset(selection.head, edits)
});
var replace = (selection, insert, caret) => {
  const { from, to } = bounds(selection);
  return { edits: [{ from, to, insert }], selection: collapsed(caret ?? from + insert.length) };
};
var editingPlan = (text, selection, action, tabSize = 2, commentPrefix = "//") => {
  if (!validSelection(text, selection)) throw new Error("Invalid selection.");
  const { from, to } = bounds(selection);
  const start = from === 0 ? 0 : text.lastIndexOf("\n", from - 1) + 1;
  const last = to > from && text[to - 1] === "\n" ? to - 1 : to;
  const endAt = text.indexOf("\n", last);
  const end = endAt < 0 ? text.length : endAt;
  const lines = text.slice(start, end).split("\n");
  const unit = " ".repeat(tabSize);
  if (action === "newline") {
    const indent = /^\s*/.exec(text.slice(start, from))[0];
    const opener = /[{[(]$/.test(text.slice(0, from));
    const inner = indent + (opener ? unit : "");
    const closing = opener && /[}\])]/.test(text[to] ?? "");
    return replace(selection, `
${inner}${closing ? `
${indent}` : ""}`, from + inner.length + 1);
  }
  if (action === "duplicate") {
    if (from !== to)
      return {
        edits: [{ from: to, to, insert: text.slice(from, to) }],
        selection: { anchor: to, head: to + to - from }
      };
    const insert = `
${text.slice(start, end)}`;
    return {
      edits: [{ from: end, to: end, insert }],
      selection: collapsed(selection.head + insert.length)
    };
  }
  if (action === "deleteLine") {
    const first = end === text.length && start > 0 ? start - 1 : start;
    const last2 = end < text.length ? end + 1 : end;
    return {
      edits: [{ from: first, to: last2, insert: "" }],
      selection: collapsed(Math.min(first, text.length - (last2 - first)))
    };
  }
  const uncomment = lines.every(
    (line) => !line.trim() || line.trimStart().startsWith(commentPrefix)
  );
  let at = start;
  const edits = [];
  for (const line of lines) {
    if (action === "indent") edits.push({ from: at, to: at, insert: unit });
    else if (action === "outdent") {
      const count = line.startsWith("	") ? 1 : Math.min(tabSize, /^ */.exec(line)[0].length);
      if (count) edits.push({ from: at, to: at + count, insert: "" });
    } else if (line.trim()) {
      const whitespace = /^\s*/.exec(line)[0].length;
      const position = at + whitespace;
      edits.push(
        uncomment ? {
          from: position,
          to: position + commentPrefix.length + (line.slice(whitespace + commentPrefix.length).startsWith(" ") ? 1 : 0),
          insert: ""
        } : { from: position, to: position, insert: `${commentPrefix} ` }
      );
    }
    at += line.length + 1;
  }
  return { edits, selection: mapSelection(selection, edits) };
};
var pairPlan = (text, selection, character) => {
  const { from, to } = bounds(selection);
  const pairs = {
    "(": ")",
    "[": "]",
    "{": "}",
    '"': '"',
    "'": "'",
    "`": "`"
  };
  if (from === to && ")]}'\"`".includes(character) && text[from] === character)
    return { edits: [], selection: collapsed(from + 1) };
  const closing = pairs[character];
  if (!closing || from === to && /[\p{L}\p{N}_$]/u.test(text[to] ?? "")) return void 0;
  if (from !== to)
    return {
      edits: [{ from, to, insert: character + text.slice(from, to) + closing }],
      selection: selection.anchor <= selection.head ? { anchor: from + 1, head: to + 1 } : { anchor: to + 1, head: from + 1 }
    };
  return replace(selection, character + closing, from + 1);
};
var findMatches = (text, query, caseSensitive, limit = 1e3) => {
  if (!query) return [];
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const expression = new RegExp(escaped, caseSensitive ? "gu" : "giu");
  const matches2 = [];
  for (const match of text.matchAll(expression)) {
    const from = match.index, to = from + match[0].length;
    if (validOffset(text, from) && validOffset(text, to)) matches2.push({ from, to });
    if (matches2.length >= limit) break;
  }
  return matches2;
};
var wordCompletions = (text, offset) => {
  const prefix = /[\p{L}\p{N}_$]*$/u.exec(text.slice(0, offset))[0];
  const rest = /^[\p{L}\p{N}_$]*/u.exec(text.slice(offset))[0];
  const words = /* @__PURE__ */ new Set([
    ...keywords,
    "string",
    "number",
    "boolean",
    "console",
    "log",
    ...[...text.matchAll(/[\p{L}_$][\p{L}\p{N}_$]*/gu)].map((match) => match[0])
  ]);
  const items = [...words].filter((word) => word.startsWith(prefix)).sort().slice(0, 200).map((label) => ({ label }));
  const list = Completion.open(offset - prefix.length, offset + rest.length, items);
  return Completion.visible(list, text, offset).length ? list : null;
};
var mapTokens = (tokens, edits) => tokens.flatMap(
  (token) => edits.some((edit) => edit.from <= token.to && edit.to >= token.from) ? [] : [{ ...token, from: mapOffset(token.from, edits), to: mapOffset(token.to, edits, -1) }]
);

// src/native/history.ts
var record = (model, edits, before, after, kind, time) => {
  const step = { edits, inverse: invert(model.document.text, edits), before, after };
  const last = model.past.at(-1);
  const priorStep = last?.steps.at(-1);
  const isComposition = kind.startsWith("composition:");
  const coalesce = last && last.kind === kind && (isComposition || ["insertText", "deleteContentBackward", "deleteContentForward"].includes(kind)) && (isComposition || time - last.time < 750) && priorStep?.after.anchor === before.anchor && priorStep.after.head === before.head && last.steps.length < 100;
  const group = {
    id: coalesce ? last.id : model.nextHistoryId,
    kind,
    time,
    steps: coalesce ? [...last.steps, step] : [step]
  };
  return {
    past: [...coalesce ? model.past.slice(0, -1) : model.past, group].slice(-100),
    future: [],
    nextHistoryId: coalesce ? model.nextHistoryId : model.nextHistoryId + 1
  };
};
var historyPlan = (model, direction) => {
  const group = (direction === "undo" ? model.past : model.future).at(-1);
  if (!group) return void 0;
  let text = model.document.text;
  for (const step of direction === "undo" ? [...group.steps].reverse() : group.steps)
    text = applyEdits(text, direction === "undo" ? step.inverse : step.edits);
  return {
    edits: difference(model.document.text, text),
    selection: direction === "undo" ? group.steps[0].before : group.steps.at(-1).after,
    groupId: group.id
  };
};

// src/native/update.ts
var Request = Schema.Struct({
  ...Identity,
  revision: Schema.Number,
  edits: Schema.Array(TextEdit),
  selection: Selection,
  kind: Schema.String,
  groupId: Schema.Number,
  focus: Schema.Boolean,
  reveal: Schema.Boolean,
  /** Reveals this offset instead of the selection's head. */
  at: Schema.optional(Schema.Number)
});
var Apply = Command.define("NativeEditorRequest", {
  args: { id: Schema.String, request: Request },
  messages: [Message.CompletedCommand],
  execute: ({ id, request: request2 }) => Effect.sync(() => {
    const input = document.getElementById(id);
    const reason = input?.nativeRequest?.(request2) ?? "The editor is not mounted.";
    return Message.CompletedCommand({ lease: request2.lease, session: request2.session, reason });
  })
});
var FocusSearch = Command.define("FocusNativeSearch", {
  args: { id: Schema.String, lease: Schema.String, session: Schema.Number },
  messages: [Message.CompletedCommand],
  execute: ({ id, lease, session }) => Effect.promise(
    () => new Promise((resolve) => {
      requestAnimationFrame(() => {
        const input = document.getElementById(id);
        if (input?.foldkitNative.lease === lease && input.foldkitNative.document.session === session)
          document.getElementById(`${id}-find`)?.focus();
        resolve(Message.CompletedCommand({ lease, session, reason: "" }));
      });
    })
  )
});
var reject = (model, reason) => ({
  model: { ...model, error: reason },
  outMessage: OutMessage.RejectedOperation({ reason })
});
var matches = (model, event) => event.session === model.document.session && event.lease === model.lease;
var sameVersion = (model, expected) => expected.uri === model.document.uri && expected.session === model.document.session && expected.revision === model.document.revision;
var request = (model, values = {}) => ({
  model,
  commands: [
    Apply({
      id: model.id,
      request: {
        lease: model.lease,
        session: model.document.session,
        revision: model.document.revision,
        edits: [],
        selection: model.selection,
        kind: "command",
        groupId: 0,
        focus: true,
        reveal: true,
        ...values
      }
    })
  ]
});
var validate = (model) => {
  const { uri, revision, session, languageId } = model.document;
  const others = model.diagnostics.filter(
    (batch) => batch.source !== "json" && batch.revision === revision && batch.session === session
  );
  return {
    ...model,
    diagnostics: [
      ...others,
      {
        uri,
        revision,
        session,
        languageId,
        source: "json",
        diagnostics: jsonDiagnostics(model.document)
      }
    ]
  };
};
var run = (model, action) => {
  if (model.composing && !["focus", "findNext", "findPrevious", "goToLine"].includes(action))
    return reject(model, "Finish composing text before running this command.");
  const text = model.document.text;
  const selection = model.selection;
  if (action === "undo" || action === "redo") {
    const plan = historyPlan(model, action);
    return plan ? request(model, { ...plan, kind: action }) : { model };
  }
  if (action === "focus") return request(model, { reveal: false });
  if (action === "format") {
    try {
      const after = JSON.stringify(JSON.parse(text), null, model.options.tabSize);
      return request(model, {
        edits: difference(text, after),
        selection: { anchor: 0, head: 0 },
        kind: "format"
      });
    } catch {
      return reject(model, "Fix the JSON syntax errors before formatting.");
    }
  }
  if (action === "goToLine") {
    const line = Math.max(0, Math.min(model.starts.length - 1, (Number(model.goToLine) || 1) - 1));
    const offset = model.starts[Math.floor(line)];
    return request(model, { selection: { anchor: offset, head: offset } });
  }
  if (action === "complete") {
    const list = model.completion;
    const choice = list === null ? void 0 : Completion.active(list, text, selection.head);
    if (list === null || !choice) return { model: { ...model, completion: null } };
    const accepted = Completion.accept(list, text, choice);
    return request(
      { ...model, completion: null },
      {
        edits: [{ from: accepted.from, to: accepted.to, insert: accepted.insert }],
        selection: { anchor: accepted.caret, head: accepted.caret },
        kind: "complete"
      }
    );
  }
  if (["findNext", "findPrevious", "replace", "replaceAll"].includes(action)) {
    const found = findMatches(text, model.search.query, model.search.caseSensitive, Infinity);
    if (!found.length) return { model };
    const next = action === "findPrevious" ? [...found].reverse().find((match) => match.to < Math.max(selection.anchor, selection.head)) ?? found.at(-1) : found.find((match) => match.from >= Math.max(selection.anchor, selection.head)) ?? found[0];
    if (action === "findNext" || action === "findPrevious")
      return request(model, { selection: { anchor: next.from, head: next.to } });
    const current = found.find(
      (match) => match.from === Math.min(selection.anchor, selection.head) && match.to === Math.max(selection.anchor, selection.head)
    );
    if (action === "replace" && !current)
      return request(model, { selection: { anchor: next.from, head: next.to } });
    const targets = action === "replaceAll" ? found : [current];
    const edits = targets.map(({ from, to }) => ({
      from,
      to,
      insert: normalizeText(model.search.replacement)
    }));
    const head = edits[0].from + edits[0].insert.length;
    return request(model, { edits, selection: { anchor: head, head }, kind: action });
  }
  return request(model, {
    ...editingPlan(
      text,
      selection,
      action,
      model.options.tabSize,
      ["yaml", "yml"].includes(model.document.languageId) ? "#" : "//"
    ),
    kind: action
  });
};
var update = (model, message) => Message.match(message, {
  Mounted: ({ lease, session }) => ({
    model: session === model.document.session ? validate({ ...model, lease, status: "Ready", composing: false, error: "" }) : model
  }),
  FailedMount: ({ reason }) => ({ model: { ...model, status: "Failed", error: reason } }),
  Edited: (event) => {
    if (!matches(model, event)) return { model };
    if (event.baseRevision !== model.document.revision)
      return reject(
        model,
        "An edit arrived for an old revision. Reload the sample to resynchronize."
      );
    try {
      const text = applyEdits(model.document.text, event.edits);
      if (!validSelection(text, event.selection))
        return reject(model, "Invalid editor selection.");
      let history;
      if (event.kind === "undo" || event.kind === "redo") {
        const group = (event.kind === "undo" ? model.past : model.future).at(-1);
        if (!group || group.id !== event.groupId)
          return reject(model, "The history changed before the command was applied.");
        history = event.kind === "undo" ? {
          past: model.past.slice(0, -1),
          future: [...model.future, group],
          nextHistoryId: model.nextHistoryId
        } : {
          past: [...model.past, group],
          future: model.future.slice(0, -1),
          nextHistoryId: model.nextHistoryId
        };
      } else
        history = record(
          model,
          event.edits,
          event.before,
          event.selection,
          event.kind,
          event.time
        );
      const document2 = { ...model.document, text, revision: model.document.revision + 1 };
      const lines = tokenize(text, document2.languageId, model.lines);
      const typing = event.kind !== "undo" && event.kind !== "redo" && event.kind !== "complete";
      const tracked = model.completion === null || !typing ? void 0 : Completion.track(model.completion, model.document.text, text, event.selection.head);
      const completion = tracked !== void 0 && Completion.visible(tracked, text, event.selection.head).length ? tracked : null;
      return {
        model: validate({
          ...model,
          ...history,
          document: document2,
          lines,
          starts: lineStarts(lines),
          selection: event.selection,
          diagnostics: [],
          error: "",
          completion,
          hover: null,
          tokens: mapTokens(model.tokens, event.edits)
        }),
        outMessage: OutMessage.ChangedDocument({
          document: document2,
          origin: event.kind === "undo" || event.kind === "redo" || event.kind === "external" ? event.kind : "input"
        })
      };
    } catch (error) {
      return reject(model, String(error));
    }
  },
  Selected: (event) => {
    if (!matches(model, event) || event.revision !== model.document.revision || !validSelection(model.document.text, event.selection))
      return { model };
    const completion = model.completion !== null && Completion.contains(model.completion, event.selection.head) && event.selection.anchor === event.selection.head ? model.completion : null;
    const hover = model.hover?.source === "Keyboard" ? null : model.hover;
    return {
      model: { ...model, selection: event.selection, completion, hover },
      outMessage: OutMessage.ChangedSelection({ selection: event.selection })
    };
  },
  Scrolled: (event) => ({
    model: matches(model, event) ? { ...model, viewport: { top: event.top, left: event.left, height: event.height } } : model
  }),
  Composition: (event) => ({
    model: matches(model, event) ? { ...model, composing: event.active, completion: null } : model
  }),
  Run: ({ action }) => run(model, action),
  Execute: ({ operation }) => {
    switch (operation._tag) {
      case "Focus":
        return run(model, "focus");
      case "Undo":
        return run(model, "undo");
      case "Redo":
        return run(model, "redo");
      case "ApplyEdits": {
        if (operation.expected.uri !== model.document.uri || operation.expected.session !== model.document.session || operation.expected.revision !== model.document.revision)
          return reject(
            model,
            "The document changed. Recompute the edits against the current revision."
          );
        if (model.options.readOnly) return reject(model, "The editor is read only.");
        if (model.composing) return reject(model, "Finish composing text before applying edits.");
        try {
          const text = applyEdits(model.document.text, operation.edits);
          const selection = operation.selection ?? mapSelection(model.selection, operation.edits);
          if (!validSelection(text, selection)) return reject(model, "Invalid editor selection.");
          return request(model, {
            edits: operation.edits,
            selection,
            kind: "external",
            focus: false,
            reveal: false
          });
        } catch (error) {
          return reject(model, String(error));
        }
      }
      case "ShowCompletions": {
        const { text } = model.document;
        const caret = model.selection.head;
        if (!sameVersion(model, operation.expected) || !validOffset(text, operation.from) || !validOffset(text, operation.to) || operation.from > operation.to || caret < operation.from || caret > operation.to)
          return { model };
        const list = Completion.open(operation.from, operation.to, operation.items);
        return {
          model: {
            ...model,
            completion: Completion.visible(list, text, caret).length ? list : null
          }
        };
      }
      case "SetSemanticTokens": {
        const { uri, session, revision, text } = { ...model.document };
        if (operation.uri !== uri || operation.session !== session || operation.revision !== revision)
          return { model };
        if (!operation.tokens.every(
          (token) => validOffset(text, token.from) && validOffset(text, token.to) && token.from <= token.to
        ))
          return reject(model, "Invalid semantic token range.");
        return {
          model: { ...model, tokens: [...operation.tokens].sort((a, b) => a.from - b.from) }
        };
      }
      case "Reveal":
        if (!sameVersion(model, operation.expected))
          return reject(model, "The document changed. Reveal against the current revision.");
        return request(model, {
          kind: "reveal",
          focus: false,
          at: Math.max(0, Math.min(operation.range.from, model.document.text.length))
        });
      case "Select":
        if (operation.expected.uri !== model.document.uri || operation.expected.session !== model.document.session || operation.expected.revision !== model.document.revision)
          return reject(model, "The document changed. Select against the current revision.");
        if (!validSelection(model.document.text, operation.selection))
          return reject(model, "Invalid editor selection.");
        return request(model, { selection: operation.selection });
      default:
        return update(model, operation);
    }
  },
  CompletedCommand: (event) => matches(model, event) && event.reason ? reject(model, event.reason) : { model },
  ReplaceDocument: ({ text, uri, languageId }) => {
    const fresh = init2({ id: model.id, text, uri, languageId, ...model.options });
    const next = {
      ...fresh,
      document: { ...fresh.document, session: model.document.session + 1 }
    };
    return {
      model: next,
      outMessage: OutMessage.ChangedDocument({ document: next.document, origin: "external" })
    };
  },
  SetOptions: ({ _tag: _, ...options }) => ({
    model: {
      ...model,
      options: {
        ...options,
        tabSize: Math.max(1, Math.min(8, Math.round(options.tabSize) || 2))
      }
    }
  }),
  SetLanguage: ({ languageId }) => {
    const lines = tokenize(model.document.text, languageId);
    const document2 = { ...model.document, languageId };
    return {
      model: validate({
        ...model,
        document: document2,
        lines,
        starts: lineStarts(lines),
        diagnostics: [],
        tokens: []
      }),
      outMessage: OutMessage.ChangedDocument({ document: document2, origin: "external" })
    };
  },
  SetDiagnostics: (batch) => {
    const document2 = model.document;
    if (batch.uri !== document2.uri || batch.session !== document2.session || batch.revision !== document2.revision || batch.languageId !== document2.languageId)
      return { model };
    if (!validDiagnostics(document2.text, batch.diagnostics))
      return reject(model, "Invalid diagnostic range.");
    const { uri, session, revision, languageId, source, diagnostics } = batch;
    return {
      model: {
        ...model,
        diagnostics: [
          ...model.diagnostics.filter((item) => item.source !== source),
          { uri, session, revision, languageId, source, diagnostics }
        ]
      }
    };
  },
  OpenSearch: ({ open }) => ({
    model: { ...model, search: { ...model.search, open } },
    commands: open ? [FocusSearch({ id: model.id, lease: model.lease, session: model.document.session })] : []
  }),
  SearchQuery: ({ query }) => ({ model: { ...model, search: { ...model.search, query } } }),
  SearchReplacement: ({ replacement }) => ({
    model: { ...model, search: { ...model.search, replacement } }
  }),
  ToggleCase: () => ({
    model: { ...model, search: { ...model.search, caseSensitive: !model.search.caseSensitive } }
  }),
  OpenCompletion: ({ open }) => {
    if (!open) return { model: { ...model, completion: null } };
    const offset = model.selection.head;
    const words = (model.options.suggestions ?? "words") === "words" ? wordCompletions(model.document.text, offset) : null;
    return {
      ...request({ ...model, completion: words ?? model.completion }, { reveal: false }),
      outMessage: OutMessage.RequestedCompletion({
        version: documentVersion(model.document),
        offset
      })
    };
  },
  MoveCompletion: ({ delta }) => {
    if (model.completion === null) return { model };
    const count = Completion.visible(
      model.completion,
      model.document.text,
      model.selection.head
    ).length;
    return { model: { ...model, completion: Completion.move(model.completion, delta, count) } };
  },
  ChooseCompletion: ({ index }) => model.completion === null ? { model } : run({ ...model, completion: { ...model.completion, index } }, "complete"),
  Hovered: (event) => {
    if (!matches(model, event) || event.revision !== model.document.revision) return { model };
    if (event.offset === null && model.hover?.source === "Keyboard") return { model };
    const hover = event.offset === null || !validOffset(model.document.text, event.offset) ? null : { offset: event.offset, source: event.source };
    return {
      model: { ...model, hover },
      outMessage: OutMessage.Hovered({
        version: documentVersion(model.document),
        offset: hover?.offset ?? null,
        source: event.source
      })
    };
  },
  DismissedHover: () => ({ model: model.hover === null ? model : { ...model, hover: null } }),
  GoToLine: ({ value }) => ({ model: { ...model, goToLine: value } }),
  Reveal: ({ selection }) => request(model, { selection })
});
var selectionOf = (input) => input.selectionDirection === "backward" ? { anchor: input.selectionEnd, head: input.selectionStart } : { anchor: input.selectionStart, head: input.selectionEnd };
var setSelection = (input, selection) => input.setSelectionRange(
  Math.min(selection.anchor, selection.head),
  Math.max(selection.anchor, selection.head),
  selection.anchor > selection.head ? "backward" : "forward"
);
var HOVER_DELAY = 350;
var ObserveInput = Mount.defineStream("ObserveNativeEditor", {
  messages: [
    Message.Mounted,
    Message.Edited,
    Message.Selected,
    Message.Scrolled,
    Message.Composition,
    Message.Run,
    Message.OpenSearch,
    Message.OpenCompletion,
    Message.MoveCompletion,
    Message.FailedMount,
    Message.Hovered,
    Message.DismissedHover
  ],
  execute: ({ element }) => Stream.callback(
    (queue) => Effect.gen(function* () {
      yield* Effect.acquireRelease(
        Effect.sync(() => {
          const input = element;
          const surface = input.closest(".native-editor__surface");
          const editorRoot = input.closest(".native-editor") ?? surface;
          const doc = input.ownerDocument;
          const mirror = surface.querySelector(".native-editor__mirror");
          const gutter = surface.querySelector(".native-editor__gutter-lines");
          let model = input.foldkitNative;
          let text = model.document.text;
          let revision = model.document.revision;
          let session = model.document.session;
          let lease = crypto.randomUUID();
          let composing = false;
          let compositionId = 0;
          let requestedCompletion = false;
          let lastSelection = model.selection;
          let pending = /* @__PURE__ */ new Map();
          let layoutFrame = 0;
          let disposed = false;
          let capturedBefore = model.selection;
          const emit = (message) => {
            if (!disposed) Queue.offerUnsafe(queue, message);
          };
          const identity = () => ({ lease, session });
          const layout = () => {
            if (disposed) return;
            mirror.style.width = `${input.clientWidth}px`;
            mirror.style.transform = `translate(${-input.scrollLeft}px, ${-input.scrollTop}px)`;
            gutter.style.transform = `translateY(${-input.scrollTop}px)`;
            if (model.options.lineWrapping) {
              const visible = mirror.querySelectorAll("[data-native-line]");
              const numbers = gutter.querySelectorAll("[data-native-number]");
              visible.forEach((line, index) => {
                if (numbers[index])
                  numbers[index].style.height = `${line.getBoundingClientRect().height}px`;
              });
            } else
              gutter.querySelectorAll("[data-native-number]").forEach((line) => {
                line.style.height = "";
              });
          };
          const scheduleLayout = () => {
            cancelAnimationFrame(layoutFrame);
            layoutFrame = requestAnimationFrame(() => {
              layout();
              if (!disposed)
                emit(
                  Message.Scrolled({
                    ...identity(),
                    top: input.scrollTop,
                    left: input.scrollLeft,
                    height: input.clientHeight
                  })
                );
            });
          };
          const select = () => {
            if (disposed || composing || input.value !== text) return;
            const selection = selectionOf(input);
            if (selection.anchor === lastSelection.anchor && selection.head === lastSelection.head)
              return;
            lastSelection = selection;
            capturedBefore = selection;
            emit(Message.Selected({ ...identity(), revision, selection }));
          };
          const commit = (edits, before, selection, kind, groupId = 0) => {
            const next = applyEdits(text, edits);
            if (!validSelection(next, selection)) throw new Error("Invalid selection.");
            const baseRevision = revision;
            if (next !== text || groupId > 0 && (kind === "undo" || kind === "redo")) {
              text = next;
              revision++;
              pending.set(revision, text);
              emit(
                Message.Edited({
                  ...identity(),
                  baseRevision,
                  edits,
                  before,
                  selection,
                  kind,
                  time: Date.now(),
                  groupId
                })
              );
            } else emit(Message.Selected({ ...identity(), revision, selection }));
            lastSelection = selection;
          };
          const apply = (plan, kind, groupId = 0, focus = true) => {
            const before = selectionOf(input);
            input.value = applyEdits(text, plan.edits);
            setSelection(input, plan.selection);
            if (focus) input.focus({ preventScroll: true });
            commit(plan.edits, before, plan.selection, kind, groupId);
            scheduleLayout();
          };
          const reconcile = (next) => {
            const changed = model.document !== next.document;
            const reset = session !== next.document.session;
            const acknowledged = pending.get(next.document.revision) === next.document.text;
            model = next;
            requestedCompletion = false;
            if (reset || changed && !acknowledged && (next.document.revision !== revision || next.document.text !== text)) {
              if (composing && !reset) return;
              text = next.document.text;
              revision = next.document.revision;
              session = next.document.session;
              pending.clear();
              input.value = text;
              setSelection(input, next.selection);
              lastSelection = next.selection;
              if (reset) {
                lease = crypto.randomUUID();
                composing = false;
                input.scrollTop = input.scrollLeft = 0;
                emit(Message.Mounted(identity()));
              }
            } else
              for (const key of pending.keys())
                if (key <= next.document.revision) pending.delete(key);
            layout();
            if (next.document !== previousRenderedDocument || next.options.lineWrapping !== previousWrapping) {
              previousRenderedDocument = next.document;
              previousWrapping = next.options.lineWrapping;
              scheduleLayout();
            }
          };
          let previousRenderedDocument = model.document;
          let previousWrapping = model.options.lineWrapping;
          input.value = text;
          setSelection(input, model.selection);
          Object.defineProperty(input, "foldkitNative", {
            configurable: true,
            get: () => model,
            set: reconcile
          });
          input.nativeRequest = (request2) => {
            if (disposed || request2.lease !== lease || request2.session !== session || request2.revision !== revision)
              return "The document changed before the command ran. Try again.";
            if (composing) return "Finish composing text before running this command.";
            if (model.options.readOnly && request2.edits.length) return "The editor is read only.";
            try {
              if (request2.kind !== "reveal") {
                apply(request2, request2.kind, request2.groupId, request2.focus);
              }
              if (request2.reveal) {
                const target = request2.at ?? request2.selection.head;
                const row = model.options.lineWrapping ? mirror.querySelectorAll("[data-native-line]")[lineAt(model.starts, target)]?.offsetTop ?? 0 : lineAt(model.starts, target) * 22 + 12;
                if (row < input.scrollTop || row + 22 > input.scrollTop + input.clientHeight)
                  input.scrollTop = Math.max(0, row - input.clientHeight / 2);
                layout();
              }
              return "";
            } catch (error) {
              return String(error);
            }
          };
          const onInput = (event) => {
            const inputEvent = event;
            const after = normalizeText(input.value);
            const edits = difference(text, after);
            const selection = selectionOf(input);
            if (edits.length)
              commit(
                edits,
                capturedBefore,
                selection,
                composing ? `composition:${lease}:${compositionId}` : inputEvent.inputType || "input"
              );
            capturedBefore = selection;
            scheduleLayout();
          };
          const beforeInput = (event) => {
            const inputEvent = event;
            capturedBefore = selectionOf(input);
            if (composing || inputEvent.isComposing || !inputEvent.cancelable || model.options.readOnly)
              return;
            if (inputEvent.inputType === "historyUndo" || inputEvent.inputType === "historyRedo") {
              event.preventDefault();
              emit(
                Message.Run({ action: inputEvent.inputType === "historyUndo" ? "undo" : "redo" })
              );
              return;
            }
            if (inputEvent.inputType === "insertText" && inputEvent.data?.length === 1) {
              const row = lineAt(model.starts, capturedBefore.head);
              const column = capturedBefore.head - model.starts[row];
              const token = model.lines[row]?.tokens.find(
                (token2) => token2.from < column && token2.to >= column
              );
              if (model.document.revision === revision && (token?.kind === "string" || token?.kind === "comment"))
                return;
              const plan = pairPlan(text, capturedBefore, inputEvent.data);
              if (plan) {
                event.preventDefault();
                apply(plan, "pair");
              }
            }
          };
          const hoverPopup = () => doc.getElementById(`${input.id}-hover`);
          const keydown = (event) => {
            if (event.isComposing || composing || event.keyCode === 229) return;
            const mod = event.ctrlKey || event.metaKey;
            const plain = !mod && !event.altKey && !event.shiftKey;
            const onScreen = doc.getElementById(`${input.id}-completions`) !== null;
            const shown = model.completion === null || !onScreen ? 0 : Completion.visible(model.completion, text, selectionOf(input).head).length;
            if (plain && (shown > 0 && ["ArrowDown", "ArrowUp", "Enter", "Escape", "Tab"].includes(event.key) || requestedCompletion && ["ArrowDown", "ArrowUp", "Enter", "Escape"].includes(event.key))) {
              event.preventDefault();
              if (event.key === "Escape") {
                requestedCompletion = false;
                emit(Message.OpenCompletion({ open: false }));
              } else if (event.key === "Enter" || event.key === "Tab") {
                requestedCompletion = false;
                emit(Message.Run({ action: "complete" }));
              } else emit(Message.MoveCompletion({ delta: event.key === "ArrowDown" ? 1 : -1 }));
              return;
            }
            if (hoverPopup() !== null) {
              clearTimeout(hoverTimer);
              if (event.key === "Escape" && plain) {
                event.preventDefault();
                emit(Message.DismissedHover());
                return;
              }
              if (model.hover?.source === "Keyboard" && !["Shift", "Control", "Alt", "Meta"].includes(event.key))
                emit(Message.DismissedHover());
            }
            if (mod && event.key.toLowerCase() === "f") {
              event.preventDefault();
              emit(Message.OpenSearch({ open: true }));
              return;
            }
            if (event.ctrlKey && event.shiftKey && event.code === "Space") {
              event.preventDefault();
              emit(
                Message.Hovered({
                  ...identity(),
                  revision,
                  offset: selectionOf(input).head,
                  source: "Keyboard"
                })
              );
              return;
            }
            if (event.ctrlKey && event.code === "Space") {
              event.preventDefault();
              requestedCompletion = true;
              emit(Message.OpenCompletion({ open: true }));
              return;
            }
            if (event.key === "Escape") {
              emit(Message.OpenCompletion({ open: false }));
              return;
            }
            if (mod && ["z", "y"].includes(event.key.toLowerCase())) {
              event.preventDefault();
              emit(
                Message.Run({
                  action: event.key.toLowerCase() === "y" || event.shiftKey ? "redo" : "undo"
                })
              );
              return;
            }
            if (model.options.readOnly) return;
            const action = event.key === "Enter" && !mod ? "newline" : mod && event.key === "]" ? "indent" : mod && event.key === "[" ? "outdent" : mod && event.key === "/" ? "comment" : mod && event.shiftKey && event.key.toLowerCase() === "k" ? "deleteLine" : event.altKey && event.shiftKey && event.key === "ArrowDown" ? "duplicate" : void 0;
            if (action) {
              event.preventDefault();
              apply(
                editingPlan(
                  text,
                  selectionOf(input),
                  action,
                  model.options.tabSize,
                  ["yaml", "yml"].includes(model.document.languageId) ? "#" : "//"
                ),
                action
              );
            }
          };
          const compositionStart = () => {
            composing = true;
            compositionId++;
            capturedBefore = selectionOf(input);
            emit(Message.Composition({ ...identity(), active: true }));
          };
          const compositionEnd = () => {
            onInput(new InputEvent("input", { inputType: "insertCompositionText" }));
            composing = false;
            emit(Message.Composition({ ...identity(), active: false }));
          };
          const scroll = () => {
            layout();
            scheduleLayout();
          };
          const blur = (event) => {
            requestedCompletion = false;
            if (event.relatedTarget instanceof Element && event.relatedTarget.closest("[data-text-popup]"))
              return;
            emit(Message.OpenCompletion({ open: false }));
          };
          let hoverTimer;
          let hoverPoint;
          const offsetUnder = (x, y) => {
            for (const line of mirror.querySelectorAll("[data-native-line]")) {
              const rect = line.getBoundingClientRect();
              if (y < rect.top || y >= rect.bottom) continue;
              const at = offsetAtPoint(line, x, y);
              return at === void 0 ? null : model.starts[Number(line.dataset.nativeLine)] + at;
            }
            return null;
          };
          const settleHover = () => {
            const point = hoverPoint;
            const under = point === void 0 ? null : doc.elementFromPoint(point.x, point.y);
            const offset = point === void 0 || under !== input ? null : offsetUnder(point.x, point.y);
            if (offset === (model.hover?.source === "Pointer" ? model.hover.offset : null))
              return;
            if (offset === null && model.hover === null) return;
            emit(Message.Hovered({ ...identity(), revision, offset, source: "Pointer" }));
          };
          const pointermove = (event) => {
            if (event.pointerType === "touch" || event.buttons !== 0) return;
            const target = event.target instanceof Element ? event.target : null;
            const hovered = [...mirror.querySelectorAll(".fw-text-hovered")].flatMap(
              (element2) => [...element2.getClientRects()]
            );
            if (target?.closest("[data-text-popup]") || isOver(hovered, event.clientX, event.clientY)) {
              clearTimeout(hoverTimer);
              return;
            }
            hoverPoint = { x: event.clientX, y: event.clientY };
            clearTimeout(hoverTimer);
            hoverTimer = setTimeout(settleHover, HOVER_DELAY);
          };
          const pointerleave = () => {
            hoverPoint = void 0;
            clearTimeout(hoverTimer);
            hoverTimer = setTimeout(settleHover, HOVER_DELAY);
          };
          const events = [
            ["beforeinput", beforeInput],
            ["input", onInput],
            ["keydown", keydown],
            ["select", select],
            ["selectionchange", select],
            ["keyup", select],
            ["pointerup", select],
            ["scroll", scroll],
            ["compositionstart", compositionStart],
            ["compositionend", compositionEnd],
            ["blur", blur]
          ];
          for (const [event, handler] of events) input.addEventListener(event, handler);
          editorRoot.addEventListener("pointermove", pointermove);
          editorRoot.addEventListener("pointerleave", pointerleave);
          const observer = new ResizeObserver(scheduleLayout);
          observer.observe(input);
          scheduleLayout();
          emit(Message.Mounted(identity()));
          return () => {
            disposed = true;
            observer.disconnect();
            cancelAnimationFrame(layoutFrame);
            clearTimeout(hoverTimer);
            for (const [event, handler] of events) input.removeEventListener(event, handler);
            editorRoot.removeEventListener("pointermove", pointermove);
            editorRoot.removeEventListener("pointerleave", pointerleave);
            delete input.nativeRequest;
            Object.defineProperty(input, "foldkitNative", {
              configurable: true,
              writable: true,
              value: model
            });
          };
        }),
        (dispose) => Effect.sync(dispose)
      );
      return yield* Effect.never;
    })
  )
});

// src/native/view.ts
var visibleLines = (model) => model.options.lineWrapping ? { from: 0, to: model.lines.length } : {
  from: Math.min(
    model.lines.length - 1,
    Math.max(0, Math.floor((model.viewport.top - 12) / 22) - 8)
  ),
  to: Math.min(
    model.lines.length,
    Math.ceil((model.viewport.top + model.viewport.height) / 22) + 8
  )
};
var touching = (ranges, from, to) => ranges.filter((range) => range.to >= from && range.from <= to);
var paintedLine = (line, start, layers, h) => {
  const end = start + line.text.length;
  const issues = touching(layers.issues, start, end);
  const pieces = segments(
    line.text,
    {
      lexical: line.tokens.map((token) => ({
        from: start + token.from,
        to: start + token.to,
        kind: token.kind
      })),
      semantic: touching(layers.semantic, start, end),
      issues,
      matches: touching(layers.matches, start, end),
      highlights: touching(layers.highlights, start, end),
      hovered: touching(layers.hovered, start, end)
    },
    start
  );
  const point = (at) => issues.some((issue) => issue.from === issue.to && issue.from === at) ? [
    h.span(
      [h.Class("native-token--point-issue"), h.DataAttribute("text-skip", "true")],
      ["\u200B"]
    )
  ] : [];
  const spans = pieces.flatMap((piece) => {
    const semantic = piece.covering.semantic.at(-1);
    const issue = mostSevere(piece.covering.issues);
    const highlight2 = piece.covering.highlights[0];
    const classes = [
      `native-token--${piece.covering.lexical[0]?.kind ?? "plain"}`,
      ...semantic === void 0 ? [] : ["native-token--semantic"],
      ...issue === void 0 ? [] : ["native-token--issue", `native-token--${issue.severity}`],
      ...piece.covering.matches.length > 0 ? ["native-token--match"] : [],
      ...highlight2 === void 0 ? [] : ["native-token--highlight"],
      ...piece.covering.hovered.length > 0 ? ["fw-text-hovered"] : []
    ];
    return [
      ...point(start + piece.from),
      h.span(
        [
          h.Class(classes.join(" ")),
          ...semantic === void 0 ? [] : [h.DataAttribute("kind", semantic.kind)],
          ...highlight2?.kind === void 0 ? [] : [h.DataAttribute("highlight", highlight2.kind)]
        ],
        [piece.text]
      )
    ];
  });
  return [
    ...spans,
    ...point(end),
    ...line.text.length ? [] : [h.span([h.DataAttribute("text-skip", "true")], ["\u200B"])]
  ];
};
var quoted = (value) => `"${value.replace(/["\\]/g, "\\$&")}"`;
var languageLabel = (languageId) => ({ json: "JSON", yaml: "YAML", typescript: "TypeScript", text: "Plain text" })[languageId] ?? languageId;
var view = (config, h) => {
  const { model, label, toParentMessage, meta, showToolbar = true, showInspector = true } = config;
  const { from, to } = visibleLines(model);
  const activeLine = lineAt(model.starts, model.selection.head);
  const issues = model.diagnostics.flatMap(
    (batch) => batch.diagnostics.map((issue) => ({ ...issue, source: batch.source }))
  );
  const matches2 = model.search.open ? findMatches(model.document.text, model.search.query, model.search.caseSensitive) : [];
  const text = model.document.text;
  const caret = model.selection.head;
  const suggestions = model.completion === null ? [] : Completion.visible(model.completion, text, caret);
  const visibleRow = (offset) => {
    const row = lineAt(model.starts, offset);
    return row >= from && row < to ? row : void 0;
  };
  const anchorAt = (offset, row) => ({
    selector: `[data-native-editor=${quoted(model.id)}] [data-native-line="${row}"]`,
    offset: offset - model.starts[row]
  });
  const completionRow = model.completion === null ? void 0 : visibleRow(model.completion.from);
  const showsCompletion = suggestions.length > 0 && completionRow !== void 0;
  const hoverAnswer = model.hover === null ? null : config.hover?.({
    offset: model.hover.offset,
    document: model.document,
    source: model.hover.source
  }) ?? null;
  const hoverIssues = model.hover === null ? [] : diagnosticsAt(issues, model.hover.offset);
  const hoverRange = hoverAnswer?.content === null && hoverIssues.length === 0 ? void 0 : hoverAnswer ?? mostSevere(hoverIssues);
  const hoverRow = hoverRange === void 0 ? void 0 : visibleRow(hoverRange.from);
  const highlights = config.highlights ?? [];
  const layers = {
    semantic: model.tokens,
    issues,
    matches: model.search.open ? matches2 : [],
    highlights,
    hovered: hoverRange === void 0 || hoverRange.to <= hoverRange.from ? [] : [hoverRange]
  };
  const button = (label2, message, disabled = false) => h.button(
    [
      h.Type("button"),
      h.Class("native-editor__button"),
      h.OnClick(toParentMessage(message)),
      ...disabled ? [h.Disabled(true)] : []
    ],
    [label2]
  );
  const action = (label2, action2, disabled = false) => button(label2, Message.Run({ action: action2 }), disabled);
  const headerMeta = meta === void 0 ? `${languageLabel(model.document.languageId)}${model.options.readOnly ? " \xB7 Read only" : ""}` : meta;
  const goToLine = () => h.div(
    [h.Class("native-editor__go-to-line")],
    [
      "Line ",
      h.input([
        h.Type("number"),
        h.Min("1"),
        h.Max(String(model.lines.length)),
        h.AriaLabel(`${label} go to line`),
        h.Value(model.goToLine),
        h.OnInput((value) => toParentMessage(Message.GoToLine({ value })))
      ]),
      action("Go", "goToLine")
    ]
  );
  return h.section(
    [
      h.Key(model.id),
      h.Class("native-editor"),
      h.DataAttribute("native-editor", model.id),
      h.DataAttribute("mode", model.options.theme),
      h.Style({ "--native-tab-size": String(model.options.tabSize) })
    ],
    [
      h.div(
        [h.Class("native-editor__heading")],
        [h.strong([], [label]), ...headerMeta === null ? [] : [h.span([], [headerMeta])]]
      ),
      ...showToolbar ? [
        h.div(
          [h.Class("native-editor__tools"), h.Role("group"), h.AriaLabel(`${label} tools`)],
          model.options.readOnly ? [button("Find", Message.OpenSearch({ open: !model.search.open })), goToLine()] : [
            action("Undo", "undo", !model.past.length),
            action("Redo", "redo", !model.future.length),
            action("Indent", "indent"),
            action("Outdent", "outdent"),
            action("Toggle comment", "comment", model.document.languageId === "json"),
            action("Duplicate", "duplicate"),
            button("Find / replace", Message.OpenSearch({ open: !model.search.open })),
            button("Suggest", Message.OpenCompletion({ open: model.completion === null })),
            ...model.document.languageId === "json" ? [action("Format JSON", "format")] : []
          ]
        )
      ] : [],
      ...model.search.open ? [
        h.div(
          [
            h.Key(`${model.id}-search`),
            h.Class("native-editor__search"),
            h.Role("group"),
            h.AriaLabel(`${label} search`)
          ],
          [
            h.input([
              h.Id(`${model.id}-find`),
              h.Type("text"),
              h.AriaLabel(`${label} find`),
              h.Placeholder("Find text"),
              h.Value(model.search.query),
              h.OnInput((query) => toParentMessage(Message.SearchQuery({ query })))
            ]),
            ...model.options.readOnly ? [] : [
              h.input([
                h.Type("text"),
                h.AriaLabel(`${label} replace with`),
                h.Placeholder("Replace with"),
                h.Value(model.search.replacement),
                h.OnInput(
                  (replacement) => toParentMessage(Message.SearchReplacement({ replacement }))
                )
              ])
            ],
            h.span(
              [h.AriaLive("polite")],
              [`${matches2.length}${matches2.length === 1e3 ? "+" : ""} matches`]
            ),
            button(
              model.search.caseSensitive ? "Match case: on" : "Match case: off",
              Message.ToggleCase()
            ),
            action("Previous", "findPrevious"),
            action("Next", "findNext"),
            ...model.options.readOnly ? [] : [action("Replace", "replace"), action("Replace all", "replaceAll")],
            button("Close search", Message.OpenSearch({ open: false }))
          ]
        )
      ] : [],
      h.div(
        [
          h.Key(`${model.id}-surface`),
          h.Class(
            `native-editor__surface${model.options.lineWrapping ? " native-editor__surface--wrap" : ""}`
          )
        ],
        [
          h.div(
            [
              h.Class("native-editor__gutter"),
              h.AriaHidden(true),
              ...!model.options.lineNumbers ? [h.Style({ display: "none" })] : []
            ],
            [
              h.div(
                [
                  h.Class("native-editor__gutter-lines"),
                  h.Style({ paddingTop: `${12 + from * 22}px` })
                ],
                model.lines.slice(from, to).map((_, index) => {
                  const row = from + index;
                  const hasIssue = issues.some((issue) => lineAt(model.starts, issue.from) === row);
                  return h.div(
                    [
                      h.DataAttribute("native-number", String(row)),
                      h.Class(
                        `${row === activeLine ? "native-editor__active-number" : ""}${hasIssue ? " native-editor__issue-number" : ""}`
                      )
                    ],
                    [String(row + 1)]
                  );
                })
              )
            ]
          ),
          h.div(
            [h.Class("native-editor__text")],
            [
              h.div(
                [h.Class("native-editor__mirror"), h.AriaHidden(true)],
                [
                  h.div([h.Style({ height: `${from * 22}px` })]),
                  ...model.lines.slice(from, to).map((line, index) => {
                    const row = from + index;
                    const lineStart = model.starts[row];
                    const highlight2 = touching(
                      highlights,
                      lineStart,
                      lineStart + line.text.length
                    )[0];
                    return h.div(
                      [
                        h.DataAttribute("native-line", String(row)),
                        h.Class(
                          `native-editor__line${row === activeLine ? " native-editor__line--active" : ""}${highlight2 === void 0 ? "" : " native-editor__line--highlighted"}`
                        ),
                        ...highlight2?.kind === void 0 ? [] : [h.DataAttribute("highlight", highlight2.kind)]
                      ],
                      paintedLine(line, lineStart, layers, h)
                    );
                  })
                ]
              ),
              h.textarea([
                h.Id(model.id),
                h.Class("native-editor__input"),
                h.AriaLabel(label),
                h.Attribute(
                  "aria-describedby",
                  `${model.id}-help${hoverRow === void 0 ? "" : ` ${model.id}-hover`}`
                ),
                h.Attribute("spellcheck", "false"),
                h.Attribute("autocapitalize", "off"),
                h.Attribute("autocomplete", "off"),
                h.Wrap(model.options.lineWrapping ? "soft" : "off"),
                h.Readonly(model.options.readOnly),
                { _tag: "Prop", key: "foldkitNative", value: model },
                ...showsCompletion ? [
                  h.AriaAutocomplete("list"),
                  h.Attribute("aria-controls", `${model.id}-completions`),
                  h.Attribute(
                    "aria-activedescendant",
                    optionId(
                      `${model.id}-completions`,
                      Math.min(model.completion.index, suggestions.length - 1)
                    )
                  )
                ] : [],
                h.OnMount(Mount.mapMessage(ObserveInput(), toParentMessage))
              ])
            ]
          )
        ]
      ),
      ...showsCompletion ? [
        CompletionPopup.view(
          {
            id: `${model.id}-completions`,
            items: suggestions,
            index: Math.min(model.completion.index, suggestions.length - 1),
            query: Completion.query(model.completion, text, caret),
            label: `${label} suggestions`,
            anchor: anchorAt(model.completion.from, completionRow),
            onChoose: (index) => toParentMessage(Message.ChooseCompletion({ index }))
          },
          h
        )
      ] : [],
      ...hoverRange === void 0 || hoverRow === void 0 ? [] : [
        HoverPopup.view(
          {
            id: `${model.id}-hover`,
            anchor: anchorAt(hoverRange.from, hoverRow),
            diagnostics: hoverIssues,
            content: hoverAnswer?.content ?? null
          },
          h
        )
      ],
      h.div(
        [h.Key(`${model.id}-status`), h.Class("native-editor__status")],
        [
          h.span(
            [],
            [`Ln ${activeLine + 1}, Col ${model.selection.head - model.starts[activeLine] + 1}`]
          ),
          h.span(
            [h.Id(`${model.id}-help`), h.Class("native-editor__help")],
            [model.options.readOnly ? "Read only" : "Tab moves focus \xB7 Ctrl/Cmd + ] indents"]
          ),
          ...!showToolbar || !model.options.readOnly ? [goToLine()] : [],
          h.span(
            [h.AriaLive("polite")],
            [model.composing ? "Composing\u2026" : `${issues.length} problems`]
          )
        ]
      ),
      ...issues.length ? [
        h.ul(
          [h.Class("native-editor__problems"), h.AriaLabel(`${label} problems`)],
          issues.map(
            (issue) => h.li(
              [],
              [
                button(
                  `${issue.severity} \xB7 ${lineAt(model.starts, issue.from) + 1}:${issue.from - model.starts[lineAt(model.starts, issue.from)] + 1} \xB7 ${issue.message}`,
                  Message.Reveal({ selection: { anchor: issue.from, head: issue.to } })
                )
              ]
            )
          )
        )
      ] : [],
      ...model.error ? [h.p([h.Class("native-editor__error"), h.Role("alert")], [model.error])] : [],
      ...showInspector ? [
        h.details(
          [h.Class("native-editor__inspector")],
          [
            h.summary([], ["Explore editor state"]),
            h.dl(
              [],
              [
                h.dt([], ["Document"]),
                h.dd(
                  [],
                  [
                    `${model.document.text.length.toLocaleString()} UTF-16 units \xB7 revision ${model.document.revision}`
                  ]
                ),
                h.dt([], ["Selection"]),
                h.dd([], [`anchor ${model.selection.anchor} \u2192 head ${model.selection.head}`]),
                h.dt([], ["Renderer"]),
                h.dd(
                  [],
                  [
                    `${to - from} of ${model.lines.length} lines \xB7 ${model.options.lineWrapping ? "wrapped layout" : "visible lines + overscan"}`
                  ]
                ),
                h.dt([], ["Undo / redo"]),
                h.dd(
                  [],
                  [
                    `${model.past.length} / ${model.future.length} groups \xB7 reversible range edits`
                  ]
                ),
                h.dt([], ["Highlighting"]),
                h.dd([], ["Incremental line lexer \xB7 cached until lexical state changes"])
              ]
            )
          ]
        )
      ] : []
    ]
  );
};

// src/code-editor.ts
var execute = (operation) => Message.Execute({ operation });
var snapshot = (model) => ({
  id: model.id,
  document: model.document,
  selection: model.selection,
  options: model.options,
  diagnostics: model.diagnostics,
  status: model.status,
  error: model.error
});
var implementation = { id: "native", Model, Message, init: init2, execute, update, view, snapshot };

// src/highlight.ts
var highlight = (code, language = "text") => tokenize(code, language).map(({ text, tokens }) => {
  const result = [];
  let offset = 0;
  for (const token of tokens) {
    if (token.from > offset) result.push({ text: text.slice(offset, token.from), kind: "plain" });
    result.push({ text: text.slice(token.from, token.to), kind: token.kind });
    offset = token.to;
  }
  if (offset < text.length) result.push({ text: text.slice(offset), kind: "plain" });
  return result;
});

export { code_editor_exports as CodeEditor, highlight };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map